import { z } from "zod";
import {
  AnalysisItemEnum,
  CrownMacroDtoSchema,
  GetScalpHistoryStatisticsRequestDtoSchema,
  GetScalpSetStatisticsRequestDtoSchema,
  JobStatusEnum,
  ScalpAverageScoreByMicroPositionSchema,
  ScalpDetailItemDtoSchema,
  ScalpHexagonScoreDtoSchema,
  ScalpMicroTrendSchema,
  ScalpTypeRatiosDtoSchema,
  ScalpTypesDtoSchema,
  SkinAverageScoreByMicroMeasureItemSchema,
} from "./scalp.dto";
import { zDateString } from "@becon-team/becon-fe-lib/my-zod";
import { BeconCommonTypes, BeconScalpTypes, BeconUserTypes } from "@becon-team/becon-fe-lib/my-types";

/** GET: 두피 분석 히스토리 조회 */

export const GetScalpHistoryResponseSchema = z.object({
  items: z.array(
    z.object({
      id: z.number(),
      analysisDate: zDateString,
      analysisItemType: z.nativeEnum(AnalysisItemEnum),
      scalpPoint: z.nativeEnum(BeconScalpTypes.ScalpPositionEnum),
      image: z.string(),
      value: z.number(),
      averages: z.object({
        my: z.number(),
        all: z.object({
          average: z.number(),
          population: z.number(),
        }),
      }),

      hexagonalScores: z.object({
        my: z.object({
          sensitivity: z.number(),
          deadSkin: z.number(),
          temperature: z.number(),
          water: z.number(),
          hairFollicleDensity: z.number(),
          hairQuantity: z.number(),
        }),
        averages: z.object({
          sensitivity: z.number(),
          deadSkin: z.number(),
          temperature: z.number(),
          water: z.number(),
          hairFollicleDensity: z.number(),
          hairQuantity: z.number(),
        }),
      }),
    }),
  ),
  totalCount: z.number(),
  skip: z.number().nullable(),
  take: z.number().nullable(),
});
export type GetScalpHistoryResponse = z.infer<typeof GetScalpHistoryResponseSchema>;

export const GetScalpHistoryRequestSchema = z.object({
  analysisItem: z.nativeEnum(AnalysisItemEnum),
  scalpPoint: z.nativeEnum(BeconScalpTypes.ScalpPositionEnum).optional(),
  skip: z.number().optional(),
  take: z.number().optional(),
  analysisDate: z.string().optional(),
  accountType: z.string().optional(),
  timeOffset: z.string(),
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
});
export type GetScalpHistoryRequest = z.infer<typeof GetScalpHistoryRequestSchema>;

export interface GetScalpHistory {
  (
    arg: {
      userId: string;
    } & GetScalpHistoryRequest,
  ): Promise<GetScalpHistoryResponse>;
}

/** GET: 두피 분석 날짜별 통계 히스토리 조회 */
export const GetScalpHistoryStatisticsResponseSchema = z.object({
  items: z.array(
    z
      .object({
        average_score: z.number(),
        analysis_date: z.string(),
      })
      .nullable(),
  ),
  skip: z.number(),
  take: z.number(),
  total_count: z.number(),
});
export type GetScalpHistoryStatisticsResponse = z.infer<typeof GetScalpHistoryStatisticsResponseSchema>;
export type GetScalpHistoryStatisticsRequest = z.infer<typeof GetScalpHistoryStatisticsRequestDtoSchema>;

export interface GetScalpHistoryStatistics {
  (arg: { userId: string } & GetScalpHistoryStatisticsRequest): Promise<GetScalpHistoryStatisticsResponse>;
}

export const GetScalpSetStatisticsResponseDtoSchema = z.object({
  items: z.array(
    z
      .object({
        average_score: z.number(),
        analysis_date: z.string(),
      })
      .nullable(),
  ),
  skip: z.number(),
  take: z.number(),
  total_count: z.number(),
});

export type GetScalpSetStatisticsRequestDto = z.infer<typeof GetScalpSetStatisticsRequestDtoSchema>;
export type GetScalpSetStatisticsResponseDto = z.infer<typeof GetScalpSetStatisticsResponseDtoSchema>;
export interface GetScalpSetStatistics {
  (arg: { userId: string } & GetScalpSetStatisticsRequestDto): Promise<GetScalpSetStatisticsResponseDto>;
}

/** GET: 정수리 분석 결과 조회 */
export const GetCrownAnalysisResultResponseSchema = z.object({
  analysis_date: zDateString,
  crown: CrownMacroDtoSchema,
});

export type GetCrownAnalysisResultResponse = z.infer<typeof GetCrownAnalysisResultResponseSchema>;

export interface GetCrownAnalysisResult {
  (arg: { userId: string; analysisId: number }): Promise<GetCrownAnalysisResultResponse>;
}

/** GET: 정수리 분석 상태 조회 */
export const GetCrownAnalysisStatusResponseSchema = z.object({
  status: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
});

export type GetCrownAnalysisStatusResponse = z.infer<typeof GetCrownAnalysisStatusResponseSchema>;

export interface GetCrownAnalysisStatus {
  (arg: { analysisId: number; userId: string }): Promise<GetCrownAnalysisStatusResponse>;
}

/** GET: 정수리 분석 히스토리 조회 */
export const GetCrownAnalysisHistoryResponseSchema = z.object({
  result: z.object({
    items: z.array(
      z.object({
        id: z.number(),
        source_images: z.nullable(
          z.object({
            normal: z.string(),
            masking: z.string(),
          }),
        ),
        user_id: z.string(),
        status: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
        reg_date: z.string(),
        analysis_date: z.string().nullable(),
      }),
    ),
    total: z.number(),
    // skip: z.number(),
    // take: z.number(),
  }),
});
export type GetCrownAnalysisHistoryResponse = z.infer<typeof GetCrownAnalysisHistoryResponseSchema>;

export interface GetCrownAnalysisHistory {
  (arg: {
    userId: string;
    skip?: number;
    take?: number;
    status: BeconCommonTypes.SetStatus;
  }): Promise<GetCrownAnalysisHistoryResponse>;
}

/** GET: 두피  마이크로 분석 트렌드 조회 */
export const GetMicroScalpAnalysisTrendResponseSchema = z.object({
  trend: z.array(ScalpMicroTrendSchema),
  average_score_by_position: z.array(ScalpAverageScoreByMicroPositionSchema),
  average_score_by_measure_item: z.array(SkinAverageScoreByMicroMeasureItemSchema),
});

export type GetMicroScalpAnalysisTrendResponse = z.infer<typeof GetMicroScalpAnalysisTrendResponseSchema>;

export interface GetMicroScalpAnalysisTrend {
  (arg: { userId: string; timeOffset: string }): Promise<GetMicroScalpAnalysisTrendResponse>;
}

/** GET: 두피 평균 점수 조회 */
export const GetScalpAverageScoreResponseSchema = z.object({
  totalAverageScore: z.number(),
});

export type GetScalpAverageScoreResponse = z.infer<typeof GetScalpAverageScoreResponseSchema>;

export interface GetScalpAverageScore {
  (arg: { userId: string }): Promise<GetScalpAverageScoreResponse>;
}

/** GET: 두피 분석 상태 조회 (query x) */
export const GetScalpAnalysisStatusResponseSchema = z.object({
  user_id: z.string(),
  status: z.nativeEnum(JobStatusEnum),
  job_type: z.string(),
  analysis_id: z.number().nullable(),
});

export type GetScalpAnalysisStatusResponse = z.infer<typeof GetScalpAnalysisStatusResponseSchema>;

export interface GetScalpAnalysisStatus {
  (arg: { userId: string; jobId: string }): Promise<GetScalpAnalysisStatusResponse>;
}

/** GET: ScalpSet Poling 조회 */
export const GetScalpsetPollingResponseSchema = z.object({
  polling: z.object({
    id: z.number(),
    status: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
  }),
});

export type GetScalpsetPollingResponse = z.infer<typeof GetScalpsetPollingResponseSchema>;

export interface GetScalpsetPolling {
  (arg: { userId: string; id: number }): Promise<GetScalpsetPollingResponse>;
}

/** GET: ScalpSet 결과 조회 */
export const GetScalpsetResultResponseSchema = z.object({
  id: z.number(),
  status: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
  analysis_date: zDateString,
  reg_date: zDateString,
  overview: z.object({
    composite_scalp_score: z.number(),
    group: z.object({
      age_group: z.nativeEnum(BeconUserTypes.AgeGroupEnum),
      gender: z.nativeEnum(BeconUserTypes.UserGenderEnum),
      num_records: z.number(),
      rank: z.number(),
      average: z.number(),
      top_10: z.number(),
    }),
    composite_score_ratio: z.array(
      z.object({
        level: z.number(),
        num_records: z.number(),
      }),
    ),
  }),
  scalp_types: ScalpTypesDtoSchema,
  scalp_type_ratios: ScalpTypeRatiosDtoSchema,
  hexagonal_scores: z.object({
    all_average: ScalpHexagonScoreDtoSchema,
    my_average: ScalpHexagonScoreDtoSchema,
  }),
  positions: z.array(
    z.object({
      position: z.nativeEnum(BeconScalpTypes.ScalpPositionEnum),
      my_average: z.number(),
      image_urls: z.array(z.string()),
    }),
  ),
  detail_items: z.object({
    forehead: z.array(ScalpDetailItemDtoSchema),
    side_head: z.array(ScalpDetailItemDtoSchema),
    hind_head: z.array(ScalpDetailItemDtoSchema),
    crown: z.array(ScalpDetailItemDtoSchema),
    forehead_center: z.array(ScalpDetailItemDtoSchema),
    forehead_left: z.array(ScalpDetailItemDtoSchema),
    forehead_right: z.array(ScalpDetailItemDtoSchema),
    side_head_left: z.array(ScalpDetailItemDtoSchema),
    side_head_right: z.array(ScalpDetailItemDtoSchema),
  }),
});

export type GetScalpsetResultResponse = z.infer<typeof GetScalpsetResultResponseSchema>;

export interface GetScalpsetResult {
  (arg: { userId: string; id: number }): Promise<GetScalpsetResultResponse>;
}

/** Delete: ScalpSet 후보 삭제  */
export interface DeleteScalpsetCandidate {
  (arg: { userId: string; id: number }): Promise<unknown>;
}

/** Post: ScalpSet 생성  */
export interface PostScalpset {
  (arg: { userId: string }): Promise<unknown>;
}

/** Post: ScalpSet 생성  */
export interface PostScalpsetDuplicate {
  (arg: { userId: string; id: number }): Promise<unknown>;
}
